import type { ReactNode } from 'react'

import { Separator } from '@/components/ui/separator'
import { cn } from '@/lib/utils'
import type { ChatParticipant, ChatPinnedMessage } from './chat-header.interface'
import { ChatHeaderActionsMenu } from './chat-header-actions-menu.molecule'
import { ChatHeaderParticipantList } from './chat-header-participant-list.molecule'
import { ChatHeaderPinnedMessages } from './chat-header-pinned-messages.molecule'
import { ChatHeaderTitleSection } from './chat-header-title-section.atom'

interface ChatHeaderToolbarProps {
  title: string
  participantList: ChatParticipant[]
  pinnedMessageList: ChatPinnedMessage[]
  isJumping?: boolean
  jumpingMessageId?: string | null
  isTitleEditing?: boolean
  isTitleSaving?: boolean
  onTitleEditStart?: () => void
  onTitleSave?: (newName: string) => void
  onTitleEditCancel?: () => void
  onParticipantClick?: (participantId: string) => void
  onPinnedMessageClick?: (messageId: string) => void
  onSettings?: () => void
  onArchive?: () => void
  onInvite?: () => void
  onLeave?: () => void
  className?: string
}

/**
 * Chat header toolbar with title, participants, pinned messages, and actions.
 * Sits at the top of a chat view, similar to Slack's channel header.
 */
export const ChatHeaderToolbar: React.FC<ChatHeaderToolbarProps> = (props): ReactNode => {
  const {
    participantList,
    pinnedMessageList,
    isJumping = false,
    jumpingMessageId = null,
    isTitleEditing = false,
    isTitleSaving = false,
  } = props

  return (
    <div
      className={cn(
        'chat-header-toolbar flex h-12 items-center justify-between gap-4 bg-muted px-4',
        props.className
      )}
    >
      <ChatHeaderTitleSection
        title={props.title}
        isEditing={isTitleEditing}
        isLoading={isTitleSaving}
        onEditStart={props.onTitleEditStart}
        onSave={props.onTitleSave}
        onCancel={props.onTitleEditCancel}
      />
      <div className="chat-header-actions-section ml-auto flex items-center gap-1">
        <ChatHeaderParticipantList
          participantList={participantList}
          onParticipantClick={props.onParticipantClick}
        />
        <Separator orientation="vertical" className="mx-1 h-5" />
        <ChatHeaderPinnedMessages
          pinnedMessageList={pinnedMessageList}
          isJumping={isJumping}
          jumpingMessageId={jumpingMessageId}
          onPinnedMessageClick={props.onPinnedMessageClick}
        />
        <Separator orientation="vertical" className="mx-1 h-5" />
        <ChatHeaderActionsMenu
          onSettings={props.onSettings}
          onArchive={props.onArchive}
          onInvite={props.onInvite}
          onLeave={props.onLeave}
          isLeaveDisabled={participantList.length <= 1}
        />
      </div>
    </div>
  )
}

ChatHeaderToolbar.displayName = 'ChatHeaderToolbar'
